import React, { useEffect, useState, useCallback } from "react";
import hapi, { API, hError } from "@/lib/health";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HospitalIcon, Search, Loader2, Send, Clock, CheckCircle2, XCircle, FileText, Pill, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: { cls: "bg-amber-50 text-amber-700", icon: Clock, label: "Pending approval" },
  approved: { cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2, label: "Connected" },
  declined: { cls: "bg-slate-100 text-slate-500", icon: XCircle, label: "Declined" },
};

export default function HospitalsTab({ accountId }) {
  const [conns, setConns] = useState(null);
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    hapi.get("/account/connections").then((r) => setConns(r.data)).catch(() => setConns([]));
    hapi.get("/account/linked-records").then((r) => setRecords(r.data)).catch(() => setRecords([]));
  }, []);
  useEffect(() => { load(); }, [load, accountId]);

  if (conns === null) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-5" data-testid="hospitals-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-800">Connected hospitals</h2>
          <Button size="sm" onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-hospital-btn"><HospitalIcon className="h-4 w-4 mr-1.5" /> Connect a hospital</Button>
        </div>
        {conns.length === 0 ? (
          <div className="text-center py-10 text-slate-400"><HospitalIcon className="h-9 w-9 mx-auto mb-2 opacity-40" /><p className="text-sm">Connect a hospital to share your records both ways.</p></div>
        ) : (
          <div className="space-y-2">
            {conns.map((c) => {
              const b = STATUS_BADGE[c.status] || STATUS_BADGE.pending;
              return (
                <div key={c.id} className="flex items-center gap-3 border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`conn-${c.id}`}>
                  <HospitalIcon className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{c.facility_name}</div><div className="text-[11px] text-slate-400">{c.organisation_name || ""}</div></div>
                  <span className={`text-[11px] font-medium rounded-full px-2.5 py-1 inline-flex items-center gap-1 ${b.cls}`} data-testid={`conn-status-${c.id}`}><b.icon className="h-3.5 w-3.5" />{b.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {records.map((r, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`linked-records-${i}`}>
          <h3 className="font-semibold text-slate-800 mb-1">{r.facility_name} <span className="text-xs font-normal text-slate-400">— records shared with you</span></h3>
          <div className="mt-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Visits & prescriptions</div>
            {(r.cases || []).length === 0 ? <p className="text-sm text-slate-400">No cases yet.</p> : (
              <div className="space-y-1.5">
                {r.cases.map((c, j) => (
                  <div key={j} className="border border-slate-100 rounded-md px-3 py-2 text-sm" data-testid={`linked-case-${i}-${j}`}>
                    <div className="flex justify-between"><span className="font-medium text-slate-800">{(c.diagnosis || []).join(", ") || c.case_no || "Consultation"}</span><span className="text-[11px] text-slate-400">{c.visit_date || ""}</span></div>
                    {(c.prescription || []).length > 0 && <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Pill className="h-3 w-3" /> {c.prescription.map((p) => p.drug).filter(Boolean).join(", ")}</div>}
                    {c.doctor_name && <div className="text-[11px] text-slate-400">Dr. {c.doctor_name}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {(r.documents || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Documents from hospital</div>
              <div className="space-y-1.5">
                {r.documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 border border-slate-100 rounded-md px-3 py-2" data-testid={`linked-doc-${d.id}`}>
                    <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span className="flex-1 text-sm text-slate-700 truncate">{d.title}</span>
                    <Button variant="outline" size="sm" onClick={() => window.open(`${API}/account/linked-records/mrd/${d.id}/file`, "_blank", "noopener,noreferrer")} data-testid={`linked-doc-view-${d.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      <ConnectDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}

function ConnectDialog({ open, onOpenChange, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setSearching(true);
    try { const { data } = await hapi.get(`/account/facilities/search?q=${encodeURIComponent(q)}`); setResults(data); }
    catch { toast.error("Search failed"); } finally { setSearching(false); }
  };
  const submit = async () => {
    if (!picked) { toast.error("Pick a hospital first"); return; }
    setBusy(true);
    try { await hapi.post("/account/connections", { facility_id: picked.facility_id, note }); toast.success("Request sent — the hospital will review it"); onOpenChange(false); setPicked(null); setNote(""); setQ(""); setResults([]); onDone(); }
    catch (e) { toast.error(hError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connect-hospital-dialog">
        <DialogHeader><DialogTitle>Connect a hospital</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())} placeholder="Search by hospital name or city" data-testid="facility-search-input" />
            <Button onClick={search} disabled={searching} className="bg-emerald-600 hover:bg-emerald-700" data-testid="facility-search-btn">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1.5">
            {results.map((f) => (
              <button key={f.facility_id} onClick={() => setPicked(f)} data-testid={`facility-option-${f.facility_id}`}
                className={`w-full text-left border rounded-md px-3 py-2 ${picked?.facility_id === f.facility_id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <div className="text-sm font-medium text-slate-800">{f.name}</div>
                <div className="text-[11px] text-slate-400">{[f.city, f.organisation_name].filter(Boolean).join(" · ")}</div>
              </button>
            ))}
            {!searching && results.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Search to find a hospital.</p>}
          </div>
          {picked && <div className="space-y-1.5"><Label>Note to hospital (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please link my records — visited last month" data-testid="connect-note" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !picked} className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-send-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send request</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
